import React, { useState, useEffect } from 'react';
import { selectColor } from '@panel/commons/core/theme';
import SimpleTooltip from '@panel/commons/lib/Tooltip/SimpleTooltip';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ReputationSelectors } from '../../ducks/reputation';
import TooltipHelper from '../../assets/icons/tooltipHelper.svg';
import IconQuality from '../../assets/icons/qualityIcon.svg';
import SectionLayout from '../SectionLayout';
import ProgressBar from '../ProgressBar';
import EmptyStateCharts from '../EmptyState';
import { dataQualityAdapter } from '../dataQualityAdapter';
import HighIconFace from '../../assets/icons/highFaceIcon.svg';
import MediumIconFace from '../../assets/icons/mediumFaceIcon.svg';
import LowIconFace from '../../assets/icons/lowFaceIcon.svg';
import NoDataIconFace from '../../assets/icons/noDataFaceIcon.svg';
import {
  Container,
  HeaderArea,
  Icon,
  TitleText,
  IconTooltip,
  TooltipComponent,
  MainArea,
  LeftSide,
  ContainerQuality,
  CircleChart,
  Separator,
  RightSide,
  ProgressBarContainer,
  QualityText,
  ProgressBarRow
} from './styles';

const Quality = ({ device, reloadDispatch }) => {
  const { t } = useTranslation();
  const [active, setActive] = useState('');
  const [qualityData, setQualityData] = useState(null);
  const [progressBarData, setProgressBarData] = useState(null);
  const { quality, qualityError } = useSelector(ReputationSelectors.getReputationState);
  const { qualityValue, totalOnlinePostings, valueHigh, valueMedium, valueLow, valueNoData } = qualityData || {};
  const isFull = qualityValue === 100;

  useEffect(() => {
    if (quality) setQualityData(dataQualityAdapter(quality));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quality]);

  useEffect(() => {
    setProgressBarData([
      { id: 'high', value: valueHigh, color: 'accessibleGreen', icon: HighIconFace },
      { id: 'medium', value: valueMedium, color: 'yellow', icon: MediumIconFace },
      { id: 'low', value: valueLow, color: 'negative', icon: LowIconFace },
      { id: 'noData', value: valueNoData, color: 'midDarkGray', icon: NoDataIconFace }
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qualityData]);

  const getTextInsideCircle = value => {
    if (value)
      return (
        <text x='18' y='21.3' className='percentage'>
          {Math.round(value)}%
        </text>
      );
    if (typeof value === 'number' && value === 0)
      return (
        <text x='18' y='20.35' className='label'>
          {t('panel.estadisticas.quality.avgQuality.noData')}
        </text>
      );
    if (!value || !Number.isNaN(value))
      return (
        <text x='18' y='20.35' className='label_noQuality'>
          {t('panel.estadisticas.quality.avgQuality.noQuality')}
        </text>
      );
    return '';
  };

  const setQuality = value => {
    if (!value && typeof value === 'number' && value === 0) return 'noData';
    if (value && value >= 90) return 'high';
    if (value && value >= 80 && value < 90) return 'medium';
    if (value && value < 80) return 'low';
    return 'noQuality';
  };

  const percentProgressBar = (valueQuality, totalElements) => {
    if (!valueQuality) return '0';
    let percentage = '0';
    if (valueQuality <= totalElements) {
      percentage = (parseInt(valueQuality) * 100) / totalElements;
    }
    if (valueQuality > totalElements) return '100';
    return percentage;
  };

  const tooltipComponentHeader = () => {
    return <TooltipComponent height='80px'>{t('panel.estadisticas.quality.tooltip')}</TooltipComponent>;
  };

  const tooltipComponent = (valueQuality, totalElements) => {
    return (
      <TooltipComponent width='146px' height='44px'>
        {t(`panel.estadisticas.quality.textBar.tooltip${valueQuality === 1 ? '' : '.plural'}`, {
          value1: valueQuality || '0',
          value2: totalElements || '0'
        })}
      </TooltipComponent>
    );
  };

  const getTopEmptyState = () => {
    if (device === 'mobile') return '20px';
    if (device === 'desktop') return '0px';
    return '30px';
  };

  return (
    <SectionLayout marginTop='0px'>
      <Container>
        <HeaderArea>
          <Icon icon={IconQuality} data-panel='panel-estadisticas-quality-icon' />
          <TitleText data-panel='panel-estadisticas-quality-title'>{t('panel.estadisticas.quality.title')}</TitleText>
          <SimpleTooltip
            offset={5}
            backgroundColor={selectColor('black')}
            isMobile={false}
            text={tooltipComponentHeader()}
            position='top-center'
            dataPanel='panel-estadisticas-quality-tooltip'
            allowPlacements='top-end'
            autoPlacements
          >
            <IconTooltip
              src={TooltipHelper}
              alt='tooltip-helper'
              width='16'
              height='16'
              data-panel='panel-estadisticas-quality-icon-tooltip'
            />
          </SimpleTooltip>
        </HeaderArea>
        {!qualityError ? (
          <MainArea device={device}>
            <LeftSide device={device}>
              <ContainerQuality device={device} qualityStatus={setQuality(qualityValue)}>
                <CircleChart
                  qualityStatus={setQuality(qualityValue)}
                  qualityNotApply={setQuality(qualityValue) === 'noQuality'}
                  data-panel='panel-estadisticas-quality-circleChart'
                  isFull={isFull}
                >
                  <div className='single-chart'>
                    <svg viewBox='0 0 36 36' className='circular-chart blue'>
                      <path
                        className='circle-bg'
                        d='M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831'
                      />
                      <path
                        className='circle'
                        strokeDasharray={`${Math.round(qualityValue)}, 100`}
                        d='M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831'
                      />
                      {getTextInsideCircle(qualityValue)}
                    </svg>
                  </div>
                  <div className='icon-container'>
                    <div className='face-icon' />
                  </div>
                </CircleChart>
                <div className='textContainer'>
                  <div className='text'>{t('panel.estadisticas.quality.avgQuality.text')}</div>
                  <div className='qualityText'>
                    {t(`panel.estadisticas.quality.avgQuality.${setQuality(qualityValue)}`)}
                  </div>
                </div>
              </ContainerQuality>
            </LeftSide>
            <Separator device={device} />
            <RightSide device={device}>
              {progressBarData &&
                progressBarData.map((element, i) => {
                  return (
                    <ProgressBarContainer
                      device={device}
                      key={i.toString()}
                      onMouseEnter={() => {
                        if (totalOnlinePostings && totalOnlinePostings >= 0) setActive(element.id);
                      }}
                      onMouseLeave={() => {
                        setActive('');
                      }}
                    >
                      <QualityText
                        device={device}
                        data-panel={`progressBar-text-quality-${element.id}`}
                        hoverActive={active === element.id}
                      >
                        <div className='progressBar-title'>{t(`panel.estadisticas.quality.textBar.${element.id}`)}</div>
                        <img
                          className='image-faces'
                          src={element.icon}
                          alt={`icon-face-${element.id}`}
                          data-panel={`icon-face-${element.id}`}
                        />
                      </QualityText>
                      <ProgressBarRow device={device} hoverActive={active === element.id}>
                        <ProgressBar
                          fillValue={percentProgressBar(element.value, totalOnlinePostings)}
                          dataPanel='quality'
                          filledColor='midDarkGray'
                          hoverColor={element.color}
                          tooltipComponent={tooltipComponent(element.value, totalOnlinePostings)}
                          hoverActive={active === element.id}
                          totalOnlinePostings={totalOnlinePostings}
                        />
                        <div className='containerValue'>
                          <div className='value' data-panel={`progressBar-quality-value-${element.id}`}>
                            {element.value || '0'}
                          </div>
                        </div>
                      </ProgressBarRow>
                    </ProgressBarContainer>
                  );
                })}
            </RightSide>
          </MainArea>
        ) : (
          <EmptyStateCharts
            withSpacing={device === 'desktop'}
            height={device === 'mobile' ? '160px' : '115px'}
            top={getTopEmptyState()}
            callback={reloadDispatch}
          />
        )}
      </Container>
    </SectionLayout>
  );
};

export default Quality;
